import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FdesDialogDraft, FdesEligibility, FdesFormulaType, FdesGeneration, FdesGenerationRequest } from '../_models/fdes';
import { NO_RETRY } from '../_utils/http-context';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class FdesService {
  // La saisie de la popup vit ici, et non dans la popup : celle-ci est détruite à chaque fermeture, et
  // l'écran formule l'est aussi dès que l'utilisateur va corriger sa formule. Une entrée par formule,
  // conservée le temps de la session.
  private readonly drafts = new Map<string, FdesDialogDraft>();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getDraft(formulaId: string): FdesDialogDraft | undefined {
    return this.drafts.get(formulaId);
  }

  saveDraft(formulaId: string, draft: FdesDialogDraft): void {
    this.drafts.set(formulaId, draft);
  }

  clearDraft(formulaId: string): void {
    this.drafts.delete(formulaId);
  }

  getFormulaTypes(formulaId: string) {
    return this.http.get<FdesFormulaType[]>(`${environment.apiUrl}/ProductFormulas/${formulaId}/fdes/formula-types`, {
      headers: this.authService.headers
    });
  }

  generate(formulaId: string, request: FdesGenerationRequest) {
    // Une génération dure plusieurs secondes et écrit une ligne d'historique : la rejouer sur un échec
    // tardif produirait une seconde FDES, et sur un refus de saturation elle doublerait la charge.
    return this.http.post<FdesGeneration>(`${environment.apiUrl}/ProductFormulas/${formulaId}/fdes`, request, {
      headers: this.authService.headers,
      context: new HttpContext().set(NO_RETRY, true)
    });
  }

  getEligibility(formulaId: string) {
    return this.http.get<FdesEligibility>(`${environment.apiUrl}/ProductFormulas/${formulaId}/fdes/eligibility`, {
      headers: this.authService.headers
    });
  }

  getHistory(formulaId: string) {
    return this.http.get<FdesGeneration[]>(`${environment.apiUrl}/ProductFormulas/${formulaId}/fdes/history`, {
      headers: this.authService.headers
    });
  }

  downloadFile(formulaId: string, generationId: string, kind: FdesDocumentKind) {
    return this.http.get(`${environment.apiUrl}/ProductFormulas/${formulaId}/fdes/${generationId}/file/${kind}`, {
      headers: this.authService.headers,
      responseType: 'blob',
      // On observe la réponse entière pour lire le nom de fichier que le serveur a composé, plutôt que
      // d'en réinventer un à chaque endroit qui télécharge.
      observe: 'response'
    });
  }
}

export type FdesDocumentKind = 'excel' | 'word' | 'xml' | 'pdf' | 'attestation';

/**
 * Déclenche le téléchargement en respectant le nom donné par le serveur. Le repli n'est pas décoratif :
 * Content-Disposition n'est lisible que si l'API l'expose en CORS, ce qu'un environnement pas encore
 * redéployé ne fait pas — le fichier garderait alors un nom de stockage (« fdes.docx ») illisible.
 */
export function saveFdesFile(response: HttpResponse<Blob>, fallbackName: string): void {
  const blob = response.body;
  if (!blob) return;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileNameFrom(response) || fallbackName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function fileNameFrom(response: HttpResponse<Blob>): string {
  const disposition = response.headers.get('Content-Disposition') || '';
  // filename*=UTF-8''… est prioritaire sur filename=… : c'est lui qui porte les accents.
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      /* en-tête malformé : on retombe sur le nom simple */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : '';
}

// Le PDF dès qu'il existe, le Word sinon : le jour où la conversion est raccordée côté serveur, le
// téléchargement bascule sans changement ici.
export function fdesDocumentKind(generation?: FdesGeneration): FdesDocumentKind | null {
  if (generation?.pdfRelativePath) {
    return 'pdf';
  }
  return generation?.wordRelativePath ? 'word' : null;
}

/**
 * Repli quand le serveur n'expose pas Content-Disposition. Même convention que `DownloadFileName` côté
 * back : le classeur porte les résultats du calcul, pas la déclaration, d'où « Donnees_ ».
 */
export function fdesFallbackFileName(kind: FdesDocumentKind, formulaName?: string, generatedDate?: string | Date): string {
  const prefix = kind === 'attestation' ? 'Attestation' : kind === 'excel' ? 'Donnees' : 'FDES';
  const extension = kind === 'excel' ? 'xlsx' : kind === 'word' ? 'docx' : kind === 'xml' ? 'xml' : 'pdf';

  const date = generatedDate ? new Date(generatedDate) : undefined;
  const stamp = date ? `${date.getFullYear()}${`${date.getMonth() + 1}`.padStart(2, '0')}${`${date.getDate()}`.padStart(2, '0')}` : '';
  const name = (formulaName || '').replace(/[^\w-]+/g, '_').replace(/_+/g, '_');

  return name ? `${prefix}_${name}_${stamp}.${extension}` : `${prefix}_${stamp}.${extension}`;
}
